import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { MapPin, ArrowUpRight, Armchair } from "lucide-react";
import { api } from "@/lib/api";
import RsvpDialog from "@/components/site/RsvpDialog";

export default function EventsSection() {
  const [events, setEvents] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [rsvpEvent, setRsvpEvent] = useState(null);

  useEffect(() => {
    api.get("/events")
      .then((r) => { setEvents(r.data); setLoaded(true); })
      .catch(() => setLoaded(true));
  }, []);

  if (loaded && events.length === 0) return null;

  return (
    <section className="bg-lima-cream text-lima-ink px-5 md:px-10 py-14 md:py-32 border-t border-black/10" data-testid="events-section">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 md:mb-20">
        <div>
          <h2 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[0.98]">
            Upcoming events <span className="font-serifit font-normal text-[#3a3a36]">&amp; workshops.</span>
          </h2>
        </div>
        <p className="max-w-sm text-sm md:text-base text-black/55 leading-relaxed">
          Open days, weekend intensives and live sessions at Lime Studios — free or low-cost, and open before Batch 01 begins.
        </p>
      </div>
      {events.length === 0 ? (
        <p className="font-mono2 text-xs tracking-[0.2em] text-black/40" data-testid="events-loading">LOADING EVENTS…</p>
      ) : (
        <div className="grid md:grid-cols-3 gap-6" data-testid="events-grid">
          {events.map((ev, i) => (
            <motion.article
              key={ev.id}
              initial={{ opacity: 0, y: 30, filter: "blur(10px)" }}
              whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
              className="bg-lima-ink border border-black group hover:-translate-y-1 transition-transform duration-300 flex flex-col"
              data-testid={`event-card-${i}`}
            >
              {ev.image && (
                <div className="relative h-48 md:h-52 overflow-hidden">
                  <img
                    src={ev.image}
                    alt={ev.title}
                    loading="lazy"
                    className="w-full h-full object-cover saturate-[.65] opacity-85 group-hover:opacity-100 group-hover:scale-105 transition-[opacity,transform] duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-lima-ink via-transparent to-transparent" />
                  <span className="absolute top-3 left-3 font-mono2 text-[10px] tracking-[0.2em] text-white bg-lima-blue px-2 py-1">
                    {ev.kind}
                  </span>
                </div>
              )}
              <div className="p-7 md:p-8 flex flex-col flex-1">
                <div className="flex items-start justify-between gap-3 mb-5">
                  <span className="font-serifd text-2xl md:text-3xl text-[#8A8FFF] leading-tight">{ev.date_text}</span>
                  {!ev.image && (
                    <span className="font-mono2 text-[10px] tracking-[0.2em] text-white/50 border border-white/20 px-2 py-1 shrink-0">
                      {ev.kind}
                    </span>
                  )}
                </div>
                <h3 className="font-display font-bold text-2xl tracking-tight leading-tight text-lima-paper">{ev.title}</h3>
                <p className="mt-3 text-sm text-white/55 leading-relaxed flex-1">{ev.description}</p>
                <div className="mt-7 pt-4 border-t border-white/10 flex items-center justify-between gap-3 font-mono2 text-[11px] tracking-[0.12em] text-white/45">
                  <span className="flex items-center gap-1.5"><MapPin size={12} /> {ev.location}</span>
                  {ev.seats && <span className="flex items-center gap-1.5 shrink-0"><Armchair size={12} /> {ev.seats}</span>}
                </div>
                <div className="mt-6 flex items-center justify-between gap-3">
                  {ev.fee ? (
                    <span className="font-display font-bold text-2xl tracking-tight text-lima-paper" data-testid={`event-fee-${i}`}>
                      {ev.fee}
                      <span className="ml-2 font-mono2 text-[10px] tracking-[0.2em] text-white/40">PER PERSON</span>
                    </span>
                  ) : <span />}
                  <button
                    onClick={() => setRsvpEvent(ev)}
                    data-testid={`event-reserve-${i}`}
                    className="self-start font-mono2 text-[11px] font-bold tracking-[0.2em] text-[#8A8FFF] border-b-2 border-lima-blue pb-1 hover:text-white transition-colors duration-200 flex items-center gap-2"
                  >
                    RESERVE A SPOT <ArrowUpRight size={13} />
                  </button>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      )}
      <RsvpDialog event={rsvpEvent} open={!!rsvpEvent} onOpenChange={(o) => !o && setRsvpEvent(null)} />
    </section>
  );
}
